// src/pages/BlogPage.tsx
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  User,
  Clock,
  ArrowRight,
  Search,
  Tag,
  Heart,
  Share2,
  Bookmark,
  TrendingUp,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Eye,
  MessageCircle,
  Filter,
  X,
  Facebook,
  Twitter,
  Linkedin,
  Link2,
} from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SEO from "../components/SEO";

gsap.registerPlugin(ScrollTrigger);

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content?: string;
  image: string;
  category: string;
  date: string;
  readTime?: number;
  author?: string;
  authorAvatar?: string;
  featured?: boolean;
  tags?: string[];
  views?: number;
  likes?: number;
  comments?: number;
}

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPost[]>([
    {
      id: 1,
      title: "The Future of Online Education: Trends to Watch in 2024",
      excerpt:
        "Discover the emerging technologies and methodologies shaping how we learn online. From AI-powered tutoring to immersive VR experiences, the future of education is here.",
      content:
        "The landscape of online education is evolving rapidly. With advancements in artificial intelligence, virtual reality, and personalized learning algorithms, students now have access to unprecedented educational opportunities. AI-powered tutoring systems can adapt to individual learning styles, providing customized feedback and guidance. Virtual reality environments allow students to immerse themselves in historical events, scientific phenomena, or complex engineering simulations. This article explores these trends and more, offering insights into how educators and learners can prepare for the future of digital learning.",
      image: "/blog-1.jpg",
      category: "Education",
      date: "Jan 15, 2024",
      readTime: 8,
      author: "Dr. Sarah Johnson",
      authorAvatar: "https://randomuser.me/api/portraits/women/68.jpg",
      featured: true,
      tags: ["Education", "Technology", "Future", "AI"],
      views: 1245,
      likes: 342,
      comments: 56,
    },
    {
      id: 2,
      title: "10 Essential Skills Every Designer Needs in 2024",
      excerpt:
        "Stay ahead of the curve with these must-have design skills. From UI/UX to motion design, discover what employers are looking for.",
      content:
        "The design industry is constantly evolving, and staying relevant requires continuous learning. In 2024, designers need to master not just traditional design principles but also emerging technologies like AI-assisted design, 3D modeling, and interactive prototyping. This comprehensive guide covers the top 10 skills that will set you apart in the competitive design landscape. Learn about design systems, user research methodologies, accessibility standards, and how to leverage AI tools to enhance your creative workflow.",
      image: "/blog-2.jpg",
      category: "Design",
      date: "Jan 12, 2024",
      readTime: 6,
      author: "Emily Chen",
      authorAvatar: "https://randomuser.me/api/portraits/women/44.jpg",
      tags: ["Design", "UI/UX", "Career", "Skills"],
      views: 892,
      likes: 234,
      comments: 45,
    },
    {
      id: 3,
      title: "How to Build a Portfolio That Gets You Hired",
      excerpt:
        "Tips and tricks for creating a standout portfolio that catches recruiters' attention and showcases your best work.",
      content:
        "Your portfolio is often the first impression potential employers have of your work. A great portfolio doesn't just showcase your best projects—it tells a story about who you are as a professional. This article provides actionable advice on selecting projects, writing case studies, presenting your work effectively, and optimizing your portfolio for both human viewers and applicant tracking systems. Learn from successful portfolios and discover common mistakes to avoid.",
      image: "/blog-3.jpg",
      category: "Career",
      date: "Jan 10, 2024",
      readTime: 5,
      author: "Michael Rodriguez",
      authorAvatar: "https://randomuser.me/api/portraits/men/32.jpg",
      tags: ["Career", "Portfolio", "Job Search"],
      views: 1567,
      likes: 567,
      comments: 89,
    },
    {
      id: 4,
      title: "The Complete Guide to Learning Python",
      excerpt:
        "Everything you need to know to start your Python journey. From basics to advanced concepts, this guide has it all.",
      content:
        "Python has become one of the most popular programming languages due to its simplicity and versatility. Whether you're a beginner looking to learn your first language or an experienced developer wanting to add Python to your toolkit, this comprehensive guide covers everything you need. Learn about Python's syntax, data structures, popular frameworks like Django and Flask, and how to build real-world applications.",
      image: "/blog-4.jpg",
      category: "Development",
      date: "Jan 8, 2024",
      readTime: 10,
      author: "David Park",
      authorAvatar: "https://randomuser.me/api/portraits/men/45.jpg",
      tags: ["Programming", "Python", "Development"],
      views: 2341,
      likes: 789,
      comments: 123,
    },
    {
      id: 5,
      title: "Marketing Strategies for Freelancers",
      excerpt:
        "Grow your freelance business with these proven marketing strategies that attract high-paying clients.",
      content:
        "As a freelancer, your success depends on your ability to market yourself effectively. This article explores proven marketing strategies that can help you attract ideal clients, build your personal brand, and grow your business. Learn about content marketing, networking strategies, pricing your services, and how to create a compelling brand story that resonates with potential clients.",
      image: "/blog-5.jpg",
      category: "Marketing",
      date: "Jan 5, 2024",
      readTime: 7,
      author: "Lisa Wong",
      authorAvatar: "https://randomuser.me/api/portraits/women/52.jpg",
      tags: ["Marketing", "Freelance", "Business"],
      views: 678,
      likes: 234,
      comments: 45,
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [likedPosts, setLikedPosts] = useState<number[]>([]);
  const [bookmarkedPosts, setBookmarkedPosts] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  const categories = ["All", "Education", "Design", "Career", "Development", "Marketing"];

  const featuredPost = posts.find(post => post.featured);
  const regularPosts = posts.filter(post => !post.featured);

  const filteredPosts = regularPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".hero-content", { opacity: 0, y: 20 }, {
        opacity: 1, y: 0, duration: 0.6, ease: "expo.out"
      });

      gsap.fromTo(".featured-post", { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.6, delay: 0.2, ease: "expo.out"
      });

      gsap.fromTo(".post-card", { opacity: 0, y: 30 }, {
        opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: "expo.out",
        scrollTrigger: {
          trigger: ".posts-grid",
          start: "top 85%",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleLike = (postId: number) => {
    setLikedPosts(prev => 
      prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]
    );
  };

  const handleBookmark = (postId: number) => {
    setBookmarkedPosts(prev => 
      prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]
    );
  };

  const sharePost = (platform: string) => {
    const url = window.location.href;
    const text = selectedPost?.title;
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text || '')}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(text || '')}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
        break;
    }
    setShowShareMenu(false);
  };

  return (
    <>
      <SEO
        title="LearnHub Blog - Expert Insights, Tutorials & Career Tips"
        description="Discover expert insights, industry trends, and practical tips on online learning, web development, data science, UI/UX design, and career advancement."
        keywords="online learning blog, tech tutorials, career advice, programming guides, data science, web development, UI/UX design, mentorship tips"
      />
      <main className="relative min-h-screen pt-20 pb-16 bg-white">
      <div className="section-container">
        {/* Hero Section */}
        <div className="max-w-3xl mx-auto mb-12 text-center hero-content">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary text-xs font-medium rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Latest Insights
          </span>
          <h1 className="mb-3 text-2xl font-bold text-black lg:text-3xl xl:text-4xl">
            LearnHub <span className="text-gradient">Blog</span>
          </h1>
          <p className="text-sm leading-relaxed lg:text-base text-gray-medium">
            Discover expert insights, industry trends, and practical tips to accelerate your learning journey.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col gap-3 mb-8 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 pr-3 text-sm bg-white border border-gray-200 rounded-lg pl-9 focus:outline-none focus:border-primary"
            />
          </div>
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg appearance-none cursor-pointer sm:w-40 focus:outline-none focus:border-primary"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Featured Post */}
        {featuredPost && !searchQuery && selectedCategory === "All" && (
          <div className="mb-12 featured-post">
            <Link to={`/blog/${featuredPost.id}`} className="block group">
              <div className="overflow-hidden transition-all duration-300 bg-white border border-gray-200 rounded-xl hover:shadow-xl hover:-translate-y-1">
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="relative h-64 overflow-hidden lg:h-full">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 bg-primary text-white text-[10px] font-medium rounded-full">
                        Featured
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full">{featuredPost.category}</span>
                      <span>•</span>
                      <span>{featuredPost.date}</span>
                      <span>•</span>
                      <span>{featuredPost.readTime} min read</span>
                    </div>
                    <h2 className="mb-3 text-xl font-bold text-black transition group-hover:text-primary">
                      {featuredPost.title}
                    </h2>
                    <p className="mb-4 text-sm leading-relaxed text-gray-600">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img
                          src={featuredPost.authorAvatar}
                          alt={featuredPost.author}
                          className="object-cover w-8 h-8 rounded-full"
                        />
                        <span className="text-xs text-gray-600">{featuredPost.author}</span>
                      </div>
                      <span className="inline-flex items-center gap-1 text-sm font-medium transition-all text-primary group-hover:gap-2">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Posts Grid */}
        <div className="grid gap-6 posts-grid md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post) => (
            <div
              key={post.id}
              className="overflow-hidden transition-all duration-300 bg-white border border-gray-200 post-card rounded-xl hover:shadow-xl hover:-translate-y-1 group"
            >
              <Link to={`/blog/${post.id}`} className="block">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-0.5 bg-white/90 text-gray-700 text-[10px] font-medium rounded-full">
                      {post.category}
                    </span>
                  </div>
                </div>
              </Link>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                  <span>{post.date}</span>
                  <span>•</span>
                  <span>{post.readTime} min read</span>
                </div>
                <Link to={`/blog/${post.id}`}>
                  <h3 className="mb-2 text-sm font-bold text-black transition line-clamp-2 group-hover:text-primary">
                    {post.title}
                  </h3>
                </Link>
                <p className="mb-3 text-xs text-gray-600 line-clamp-2">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <button
                      onClick={() => handleLike(post.id)}
                      className={`flex items-center gap-1 transition ${likedPosts.includes(post.id) ? 'text-red-500' : 'hover:text-red-500'}`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${likedPosts.includes(post.id) ? 'fill-red-500' : ''}`} />
                      <span>{post.likes! + (likedPosts.includes(post.id) ? 1 : 0)}</span>
                    </button>
                    <button
                      onClick={() => handleBookmark(post.id)}
                      className={`flex items-center gap-1 transition ${bookmarkedPosts.includes(post.id) ? 'text-primary' : 'hover:text-primary'}`}
                    >
                      <Bookmark className={`w-3.5 h-3.5 ${bookmarkedPosts.includes(post.id) ? 'fill-primary' : ''}`} />
                    </button>
                    <div className="flex items-center gap-1">
                      <MessageCircle className="w-3.5 h-3.5" />
                      <span>{post.comments}</span>
                    </div>
                  </div>
                  <Link
                    to={`/blog/${post.id}`}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Read More
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="py-12 text-center">
            <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <h3 className="mb-1 text-base font-semibold text-black">No articles found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or filter</p>
          </div>
        )}
      </div>

      {/* Blog Post Modal */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto bg-black/50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="relative h-64 overflow-hidden">
              <img
                src={selectedPost.image}
                alt={selectedPost.title}
                className="object-cover w-full h-full"
              />
              <button
                onClick={() => setSelectedPost(null)}
                className="absolute p-2 text-white transition rounded-lg top-4 right-4 bg-black/50 hover:bg-black/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                <span className="px-2 py-0.5 bg-gray-100 rounded-full">{selectedPost.category}</span>
                <span>•</span>
                <span>{selectedPost.date}</span>
                <span>•</span>
                <span>{selectedPost.readTime} min read</span>
              </div>
              <h1 className="mb-3 text-xl font-bold text-black">{selectedPost.title}</h1>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={selectedPost.authorAvatar}
                  alt={selectedPost.author}
                  className="object-cover w-10 h-10 rounded-full"
                />
                <div>
                  <p className="text-sm font-medium text-black">{selectedPost.author}</p>
                  <p className="text-xs text-gray-500">Author</p>
                </div>
              </div>
              <div className="mb-6 leading-relaxed prose-sm prose text-gray-700 max-w-none">
                <p>{selectedPost.content}</p>
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedPost.tags?.map(tag => (
                  <span key={tag} className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleLike(selectedPost.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition ${likedPosts.includes(selectedPost.id) ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    <Heart className={`w-4 h-4 ${likedPosts.includes(selectedPost.id) ? 'fill-red-500' : ''}`} />
                    {selectedPost.likes! + (likedPosts.includes(selectedPost.id) ? 1 : 0)}
                  </button>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-gray-600">
                    <Eye className="w-4 h-4" />
                    {selectedPost.views}
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-gray-600">
                    <MessageCircle className="w-4 h-4" />
                    {selectedPost.comments}
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 transition"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  {showShareMenu && (
                    <div className="absolute right-0 z-10 w-48 py-2 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
                      <button onClick={() => sharePost('facebook')} className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50">
                        <Facebook className="w-4 h-4 text-blue-600" /> Facebook
                      </button>
                      <button onClick={() => sharePost('twitter')} className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50">
                        <Twitter className="w-4 h-4 text-sky-500" /> Twitter
                      </button>
                      <button onClick={() => sharePost('linkedin')} className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50">
                        <Linkedin className="w-4 h-4 text-blue-700" /> LinkedIn
                      </button>
                      <button onClick={() => sharePost('copy')} className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50">
                        <Link2 className="w-4 h-4 text-gray-500" /> Copy Link
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .text-gradient {
          background: linear-gradient(135deg, #E85D04 0%, #F48C06 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      </main>
      </>
  );
};

export default BlogPage;